/**
 * Transfer audit of the deferred mailbox (OPTIONAL account).
 *
 * The signaling relay keeps, per account, an append-only and
 * bounded log of mailbox FACTS (`apps/signaling/src/mailbox.rs`): deposit of an
 * envelope by a sender (possibly a guest), pickup by the recipient, and addition
 * or revocation of trusted devices. It reuses the mental model of
 * `activity` — each row is a fact forward, it is never rewritten.
 *
 * This module is PURE (no network, no DOM): it only types and validates the entries
 * the server returns. Metadata only — the relay remains BLIND: the
 * audit never contains content, keys or IPs.
 */

import { isRecord } from "../p2p/guards";

/** Auditable actions (stable tokens, in parity with the Rust enum). */
export const AUDIT_ACTIONS = ["deposited", "picked_up", "device_added", "device_revoked"] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** An audit entry, already validated and in camelCase. */
export interface AuditEntry {
  id: string;
  action: AuditAction;
  /** Device the fact refers to (recipient, or the added/revoked one). */
  deviceId: string;
  /** Envelope size in bytes (only `deposited`/`picked_up`). */
  bytes?: number;
  /** Instant of the fact (Unix seconds). */
  atUnix: number;
}

/** `true` if `raw` is a known action token (defense against foreign wire). */
export function isAuditAction(raw: unknown): raw is AuditAction {
  return typeof raw === "string" && (AUDIT_ACTIONS as readonly string[]).includes(raw);
}

/**
 * Validates and normalizes a wire entry (`{ id, action, deviceId, bytes?,
 * atUnix }`). Returns `null` if a required field is missing or the action is
 * unknown — the caller discards invalid entries without breaking the list.
 */
export function parseAuditEntry(raw: unknown): AuditEntry | null {
  if (
    !isRecord(raw) ||
    typeof raw.id !== "string" ||
    !isAuditAction(raw.action) ||
    typeof raw.deviceId !== "string" ||
    typeof raw.atUnix !== "number"
  ) {
    return null;
  }
  const entry: AuditEntry = {
    id: raw.id,
    action: raw.action,
    deviceId: raw.deviceId,
    atUnix: raw.atUnix,
  };
  if (typeof raw.bytes === "number") {
    entry.bytes = raw.bytes;
  }
  return entry;
}
